import { useEffect, useRef } from 'react';

import { canBuildDurableProfile } from './consent';
import { startAnalyticsContextSync } from './contextSync';
import { consumeLoginAttempt } from './loginAttempt';
import { isAnalyticsEnabled, posthog } from './posthog';
import { track } from './track';

import { useAuth } from '@/shared/hooks/useAuth';
import { isAppPath } from '@/shared/routes/paths';
import { useBrandStore } from '@/shared/stores/useBrandStore';

import type { User } from '@/shared/types/user';

/**
 * /auth/me returns `firstName`/`lastName`, never a joined `name`, so building
 * one here is the difference between a named person profile and a nameless one.
 * Returns null rather than an empty string when neither part is present, so
 * PostHog stores no name instead of a blank one.
 */
const displayName = (user: User): string | null => {
  const joined = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return joined === '' ? (user.name ?? null) : joined;
};

/**
 * Owns the two PostHog calls that carry state of their own: `identify` (person
 * profiles) and `group` (group analytics). Everything that is just an event
 * property — org, brand, role, page — is attached at send time by the
 * `before_send` hook in ./posthog, fed by ./contextSync.
 */
const AnalyticsBootstrap: React.FC = () => {
  const user = useAuth((s) => s.user);
  const brandId = useBrandStore((s) => s.brandId);

  const lastIdentifiedUserId = useRef<string | null>(null);
  const lastOrgId = useRef<string | null>(null);
  const lastBrandId = useRef<string | null>(null);

  // Started unconditionally: the context module is inert without PostHog, and
  // this keeps the subscription lifecycle independent of env configuration.
  useEffect(() => startAnalyticsContextSync(), []);

  useEffect(() => {
    if (!isAnalyticsEnabled) return;

    // identify()/group() write a durable person profile, which is exactly what
    // a visitor who declined the sale/sharing of personal information opted out
    // of. Event capture may still be on (limited consent); only the persistent
    // profile is withheld here.
    if (!canBuildDurableProfile()) {
      lastIdentifiedUserId.current = null;
      lastOrgId.current = null;
      lastBrandId.current = null;
      return;
    }

    if (!user) {
      lastIdentifiedUserId.current = null;
      lastOrgId.current = null;
      lastBrandId.current = null;
      return;
    }

    if (lastIdentifiedUserId.current !== user.id) {
      // Every property here comes off the WorkOS session, never off a form
      // field. A user can type a colleague's address into an invite input; that
      // address must not become the identity of THIS browser. Guarded by a test
      // in __tests__/AnalyticsBootstrap.test.tsx.
      posthog.identify(user.id, {
        email: user.email,
        name: displayName(user),
        role: user.role ?? null,
        organization_id: user.organization?.id ?? null
      });
      lastIdentifiedUserId.current = user.id;

      // Emitted here rather than in useAuth.checkAuth: capturing before
      // identify() lands the event on the anonymous distinct_id, and with
      // person_profiles: 'identified_only' it then never attaches to the person
      // and can never join a funnel. The ref guard above already gives us
      // exactly-once-per-new-user semantics.
      const attempt = consumeLoginAttempt();
      const organizationCount = user.organizations?.length ?? 0;

      if (attempt) {
        track('auth_login_completed', {
          method: 'workos',
          login_attempt_id: attempt.id,
          round_trip_ms: Date.now() - attempt.startedAt,
          organization_count: organizationCount
        });
      } else {
        track('auth_session_restored', {
          organization_count: organizationCount,
          is_app_route:
            typeof window !== 'undefined' && isAppPath(window.location.pathname)
        });
      }
    }

    if (user.organization && lastOrgId.current !== user.organization.id) {
      posthog.group('organization', user.organization.id, {
        name: user.organization.name,
        status: user.organization.status,
        workos_id: user.organization.workosOrganizationId ?? null
      });
      lastOrgId.current = user.organization.id;
    }
  }, [user]);

  useEffect(() => {
    if (!isAnalyticsEnabled) return;
    // Same durable-profile gate as the identify effect above: a brand group is
    // part of the persistent profile a sale/sharing opt-out declines.
    if (!canBuildDurableProfile()) {
      lastBrandId.current = null;
      return;
    }

    const brand = brandId
      ? user?.brands.find((b) => b.id === brandId)
      : undefined;

    if (!brand) {
      lastBrandId.current = null;
      return;
    }

    if (lastBrandId.current !== brand.id) {
      posthog.group('brand', brand.id, {
        name: brand.name,
        status: brand.status,
        organization_id: user?.organization?.id ?? null
      });
      lastBrandId.current = brand.id;
    }
  }, [brandId, user]);

  return null;
};

export default AnalyticsBootstrap;
